import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fuel, Facebook, Twitter, Linkedin, Mail } from 'lucide-react';
import NewsletterModal from './NewsletterModal';

// Extend Window interface for MailerLite
declare global {
  interface Window {
    ml?: (command: string, accountId: string) => void;
  }
}

// Scroll to top helper function
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  // Load MailerLite script
  useEffect(() => {
    // Check if script is already loaded
    if (window.ml) return;

    const script = document.createElement('script');
    script.src = 'https://assets.mailerlite.com/js/universal.js';
    script.async = true;

    script.onload = () => {
      if (window.ml) {
        window.ml('account', '1830380');
      }
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  const sections = [
    {
      title: 'Services',
      links: [
        { label: 'Property Valuation', href: '/valuation' },
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Insights', href: '/blog' }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'FAQ', href: '/faq' },
        { label: 'Contact', href: '/contact-us' },
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Refund Policy', href: '/refund-policy' }
      ]
    },
    {
      title: 'Newsletter',
      isNewsletter: true
    }
  ];

  return (
    <footer className="bg-white border-t">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <Fuel className="w-8 h-8 text-fc-orange-500" />
              <span className="font-bold text-xl">Fuel Central</span>
            </Link>
            <p className="text-gray-600 mb-4">
              Empowering retail petroleum market participants with data-driven insights for smarter business decisions.
            </p>
            <div className="flex flex-col space-y-2 text-sm text-gray-600 mb-6">
              <a href="mailto:contact@thefuelcentral.com" className="hover:text-blue-600 transition-colors">
                contact@thefuelcentral.com
              </a>
              <a href="tel:+19512235610" className="hover:text-blue-600 transition-colors">
                +1 (951) 223-5610
              </a>
            </div>
            {/* Social media icons hidden */}
            <div className="hidden">
              <a href="#" className="text-gray-400 hover:text-blue-600 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-600 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-400 hover:text-blue-600 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="mailto:contact@thefuelcentral.com" className="text-gray-400 hover:text-blue-600 transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="font-semibold mb-4">{section.title}</h3>
              {section.isNewsletter ? (
                <div>
                  <p className="text-gray-600 text-sm mb-4">
                    Stay updated with the latest industry insights and market trends.
                  </p>
                  <button
                    onClick={() => setShowNewsletterModal(true)}
                    className="bg-fc-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-fc-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Subscribe
                  </button>
                </div>
              ) : (
                <ul className="space-y-3">
                  {section.links?.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                        onClick={link.href === '/privacy' || link.href === '/terms' || link.href === '/refund-policy' ? scrollToTop : undefined}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="border-t mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600">
            {currentYear} Fuel Central. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-gray-600 hover:text-blue-600" onClick={scrollToTop}>
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-600 hover:text-blue-600" onClick={scrollToTop}>
              Terms of Service
            </Link>
            <Link to="/contact-us" className="text-gray-600 hover:text-blue-600" onClick={scrollToTop}>
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Newsletter Modal */}
      <NewsletterModal
        isOpen={showNewsletterModal}
        onClose={() => setShowNewsletterModal(false)}
      />
    </footer>
  );
}